import { useState } from 'react';
import type { Category } from '../types';
import { CATEGORIES } from '../data/questions';
import { CATEGORY_COLORS } from '../lib/categoryUtils';

interface ProgressBarProps {
  categories: Category[];
  answeredByCategory: Record<Category, number>;
  totalByCategory: Record<Category, number>;
}

export default function ProgressBar({
  categories,
  answeredByCategory,
  totalByCategory,
}: ProgressBarProps) {
  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);

  const totalQuestions = categories.reduce((sum, cat) => sum + (totalByCategory[cat] || 0), 0);
  const totalAnswered = categories.reduce((sum, cat) => sum + (answeredByCategory[cat] || 0), 0);

  const categoryColors = CATEGORY_COLORS;

  return (
    <div className="w-full">
      {/* Overall progress text */}
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs font-medium text-slate-400">
          Progress
        </span>
        <span className="text-xs text-slate-400">
          {totalAnswered} / {totalQuestions}
        </span>
      </div>

      {/* Segmented bar */}
      <div className="relative flex h-2.5 rounded-full overflow-hidden bg-white/10">
        {categories.map((cat) => {
          const total = totalByCategory[cat] || 0;
          const answered = answeredByCategory[cat] || 0;
          if (total === 0) return null;
          const segmentWidth = (total / totalQuestions) * 100;
          const fillPercent = (answered / total) * 100;
          const meta = CATEGORIES.find((c) => c.id === cat);

          return (
            <div
              key={cat}
              className="relative h-full"
              style={{ width: `${segmentWidth}%` }}
              onMouseEnter={() => setHoveredCategory(cat)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              <div
                className={`h-full ${categoryColors[cat]} transition-all duration-300 ease-out`}
                style={{ width: `${fillPercent}%` }}
              />

              {/* Tooltip */}
              {hoveredCategory === cat && meta && (
                <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-deep-purple border border-white/20 text-white text-xs whitespace-nowrap z-10 pointer-events-none">
                  {meta.emoji} {meta.label}: {answered}/{total}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
